import {
  AttachmentChip,
  AttachmentIcon,
} from '@/components/AgentChat/AttachmentChip';
import { getAdsPlatformIcon } from '@/utils/assets';
import { AgentRunAttachment } from '@superfunnel/types';
import { ScrollArea, ScrollBar } from '@superfunnel/ui-core/v2/ScrollArea';
import { classNames } from '@superfunnel/utils';
import { FrameSelect } from 'iconoir-react';

/** Inline Figma logo SVG */
function FigmaIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 38 57"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19 28.5C19 23.2533 23.2533 19 28.5 19C33.7467 19 38 23.2533 38 28.5C38 33.7467 33.7467 38 28.5 38C23.2533 38 19 33.7467 19 28.5Z"
        fill="#1ABCFE"
      />
      <path
        d="M0 47.5C0 42.2533 4.25329 38 9.5 38H19V47.5C19 52.7467 14.7467 57 9.5 57C4.25329 57 0 52.7467 0 47.5Z"
        fill="#0ACF83"
      />
      <path
        d="M19 0V19H28.5C33.7467 19 38 14.7467 38 9.5C38 4.25329 33.7467 0 28.5 0H19Z"
        fill="#FF7262"
      />
      <path
        d="M0 9.5C0 14.7467 4.25329 19 9.5 19H19V0H9.5C4.25329 0 0 4.25329 0 9.5Z"
        fill="#F24E1E"
      />
      <path
        d="M0 28.5C0 33.7467 4.25329 38 9.5 38H19V19H9.5C4.25329 19 0 23.2533 0 28.5Z"
        fill="#A259FF"
      />
    </svg>
  );
}

interface ChatMessageAttachmentsProps {
  attachments: AgentRunAttachment[];
  className?: string;
}

const AttachmentItem = ({ attachment }: { attachment: AgentRunAttachment }) => {
  if (attachment.type === 'campaign') {
    return (
      <AttachmentChip
        label={attachment.name}
        tooltip={attachment.name}
        icon={
          <img
            src={getAdsPlatformIcon(attachment.provider)}
            alt={attachment.provider}
            className="ml-0.5 block size-6"
          />
        }
      />
    );
  }

  if (attachment.type === 'file') {
    return (
      <AttachmentChip
        label={attachment.name}
        tooltip={attachment.name}
        icon={
          <AttachmentIcon
            mimeType={attachment.mimeType}
            src={attachment.url}
            name={attachment.name}
          />
        }
      />
    );
  }

  if (attachment.type === 'element') {
    const displayText =
      attachment.elementText || attachment.elementTag || 'Selected Element';
    const truncatedText =
      displayText.length > 30
        ? displayText.substring(0, 30) + '...'
        : displayText;

    return (
      <AttachmentChip
        label={truncatedText}
        tooltip={displayText}
        icon={<FrameSelect className="text-base-500 ms-1 size-4" />}
      />
    );
  }

  if (attachment.type === 'asset') {
    const assetName = attachment.asset.alt || 'Image asset';
    const thumbnailUrl = attachment.asset.thumbnail || attachment.asset.url;

    return (
      <AttachmentChip
        label={assetName}
        tooltip={assetName}
        icon={
          thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={assetName}
              className="size-7.5 rounded-lg object-cover"
            />
          ) : (
            <AttachmentIcon mimeType="image/jpeg" name={assetName} />
          )
        }
      />
    );
  }

  if (attachment.type === 'figma') {
    const figmaName = attachment.name || 'Figma Export';

    return (
      <AttachmentChip
        label={figmaName}
        tooltip={`${figmaName} · ${attachment.layerCount} layers · ${attachment.width}×${attachment.height}`}
        icon={
          attachment.screenshotUrl ? (
            <img
              src={attachment.screenshotUrl}
              alt={figmaName}
              className="size-7.5 rounded-lg object-cover"
            />
          ) : (
            <FigmaIcon className="ms-1 size-4" />
          )
        }
      />
    );
  }

  return null;
};

export const ChatMessageAttachments = ({
  attachments,
  className,
}: ChatMessageAttachmentsProps) => {
  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className={classNames('mb-2', className)}>
      <ScrollArea className="relative w-full rounded-md whitespace-nowrap">
        <div className="flex items-center gap-2">
          {attachments.map((attachment, index) => (
            <AttachmentItem
              key={`${attachment.type}-${attachment.id || index}`}
              attachment={attachment}
            />
          ))}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
};
